import { Game } from '../types';
import { supabase } from '../lib/supabase';

/**
 * Configuração do sistema de onboarding
 */
export const ONBOARDING_CONFIG = {
  // Ativar ou desativar o sistema de onboarding
  enabled: true,
  
  // Definir se os jogos serão específicos ou populares
  usePopularGames: false,
  
  // Lista de nomes de jogos para recomendar durante o onboarding
  // (usado apenas se usePopularGames for false)
  suggestedGames: [
    'Banco Imobiliário',
    'Pandemic',
    'Ticket to Ride',
    'Azul',
    'Carcassonne',
    '7 Wonders',
    'Dixit',
    'Catan: O Jogo'
  ],
  
  // Quantidade de jogos populares a exibir (se usePopularGames for true)
  popularGamesCount: 10,
  
  // Textos do onboarding (podem ser personalizados)
  texts: {
    welcome: {
      title: 'Bem-vindo à nossa plataforma!',
      description: 'Estamos felizes em ter você aqui. Vamos começar preparando sua experiência personalizada.',
    },
    selectGames: {
      title: 'Selecione alguns jogos',
      description: 'Escolha jogos para adicionar à sua coleção, lista de desejos ou marcar como jogados. Isso nos ajudará a recomendar jogos mais relevantes para você.',
    },
    collectionHelp: 'Adicione à sua coleção os jogos que você possui.',
    wishlistHelp: 'Adicione à sua lista de desejos os jogos que você gostaria de adquirir.',
    playedHelp: 'Marque os jogos que você já jogou, mesmo que não os possua.',
    buttonContinue: 'Continuar para a página inicial',
    skipText: 'Pular por enquanto',
  }
};

/**
 * Interface para carregar jogos por nome para o onboarding
 */
export async function loadSuggestedGamesByName(gameNames: string[]): Promise<Game[]> {
  try {
    if (!gameNames || gameNames.length === 0) {
      console.error("[ONBOARDING] Lista de nomes de jogos vazia");
      return [];
    }

    // Jogos populares que precisam ser encontrados exatamente
    const popularGames = new Set([
      'catan', 'pandemic', 'ticket to ride', 'monopoly', 'scrabble', 'risk',
      'clue', 'azul', 'carcassonne', '7 wonders', 'dixit', 'splendor', 'codenames',
      'dominion', 'uno', 'stratego', 'chess', 'checkers', 'backgammon', 'go'
    ]);
    
    // Dividir a busca entre jogos populares e outros jogos
    const popularSearchNames: string[] = [];
    const otherSearchNames: string[] = [];
    
    gameNames.forEach(name => {
      if (popularGames.has(name.toLowerCase())) {
        popularSearchNames.push(name);
      } else {
        otherSearchNames.push(name);
      }
    });
    
    // Realizar buscas separadas para jogos populares (com correspondência exata)
    // e outros jogos (com correspondência mais flexível)
    let allFoundGames: Game[] = [];
    
    // 1. Busca exata para jogos populares
    if (popularSearchNames.length > 0) {
      const exactQueries = popularSearchNames.map(name => {
        // Aqui usamos '=' para correspondência exata, não ilike
        return `name.ilike.${name}`;
      });
      
      const { data: popularData, error: popularError } = await supabase
        .from('games')
        .select('*')
        .or(exactQueries.join(','));
      
      if (popularError) {
        console.error("[ONBOARDING] Erro ao buscar jogos populares:", popularError);
      } else if (popularData) {
        allFoundGames = [...popularData];
      }
      
      // Segunda tentativa para jogos populares com nome exato - mais rigorosa
      const missingPopularGames = popularSearchNames.filter(name => 
        !allFoundGames.some(game => game.name.toLowerCase() === name.toLowerCase())
      );
      
      if (missingPopularGames.length > 0) {
        // Iterar para cada jogo popular que não foi encontrado e tentar buscar
        // de uma forma mais específica
        for (const name of missingPopularGames) {
          const { data: exactData, error: exactError } = await supabase
            .from('games')
            .select('*')
            .eq('name', name) // Busca por nome exato
            .limit(1);
          
          if (!exactError && exactData && exactData.length > 0) {
            allFoundGames.push(exactData[0]);
          } else {
            // Tenta uma busca mais específica para casos como "Catan" vs "Yucatan"
            const lowerName = name.toLowerCase();
            
            const { data: wordData, error: wordError } = await supabase
              .from('games')
              .select('*')
              .or(`name.ilike.% ${name} %,name.ilike.${name} %,name.ilike.% ${name},name.eq.${name}`);
            
            if (!wordError && wordData && wordData.length > 0) {
              // Encontrar o resultado mais relevante usando critérios específicos
              const exactMatchIndex = wordData.findIndex(game => 
                game.name.toLowerCase() === lowerName
              );
              
              if (exactMatchIndex >= 0) {
                allFoundGames.push(wordData[exactMatchIndex]);
              } else {
                // Encontrar jogos que têm o nome como uma palavra inteira, não parte de outra palavra
                const wordBoundaryMatches = wordData.filter(game => {
                  const gameName = game.name.toLowerCase();
                  const words = gameName.split(/\s+/);
                  return words.includes(lowerName);
                });
                
                if (wordBoundaryMatches.length > 0) {
                  // Ordenar para favorecer nomes mais curtos
                  wordBoundaryMatches.sort((a, b) => a.name.length - b.name.length);
                  allFoundGames.push(wordBoundaryMatches[0]);
                } else {
                  // Se ainda não encontrou, pegar o resultado mais curto
                  wordData.sort((a, b) => a.name.length - b.name.length);
                  allFoundGames.push(wordData[0]);
                }
              }
            }
          }
        }
      }
    }
    
    // 2. Busca para os jogos restantes (não populares)
    if (otherSearchNames.length > 0) {
      const partialQueries = otherSearchNames.map(name => {
        return `name.ilike.%${name}%`;
      });
      
      const { data: otherData, error: otherError } = await supabase
        .from('games')
        .select('*')
        .or(partialQueries.join(','));
        
      if (otherError) {
        console.error("[ONBOARDING] Erro ao buscar jogos não populares:", otherError);
      } else if (otherData) {
        allFoundGames = [...allFoundGames, ...otherData];
      }
    }
    
    // Função para verificar se um jogo parece ser uma expansão/variante
    const isLikelyExpansion = (gameName: string, searchName: string): boolean => {
      // Verifica se o nome contém indicadores comuns de expansões/edições especiais
      const expansionIndicators = [
        'expansion', 'expansão', 'addon', 'add-on', 
        'edition', 'edição', 'collector', 'colecionador',
        'deluxe', 'premium', 'special', 'especial',
        'gamer', 'bundle', 'pack', 'promo', 'variant', 'variante',
        ':'
      ];
      
      // Se o nome do jogo termina com o nome da busca, é provavelmente o jogo base
      if (gameName.toLowerCase() === searchName.toLowerCase()) return false;
      
      // Se o nome do jogo é muito mais longo que o nome da busca, é suspeito
      if (gameName.length > searchName.length * 2) return true;
      
      // Verifica indicadores comuns de expansão/variante no nome
      return expansionIndicators.some(indicator => 
        gameName.toLowerCase().includes(indicator.toLowerCase())
      );
    };

    // Processar os resultados para cada nome buscado
    const result: Game[] = [];
    const foundNames = new Set<string>();
    
    // Processar primeiro os jogos populares para garantir que tenham prioridade
    for (const searchName of gameNames) {
      const searchLower = searchName.toLowerCase();
      
      // Para jogos populares, procurar correspondência exata primeiro
      if (popularGames.has(searchLower)) {
        // 1. Correspondência exata no nome
        const exactMatch = allFoundGames.find(game => 
          !foundNames.has(game.id) && 
          game.name.toLowerCase() === searchLower
        );
        
        if (exactMatch) {
          result.push(exactMatch as Game);
          foundNames.add(exactMatch.id);
          continue;
        }
        
        // 2. O nome do jogo contém o termo exato como uma palavra separada
        const wordMatch = allFoundGames.find(game => {
          if (foundNames.has(game.id)) return false;
          
          const gameName = game.name.toLowerCase();
          const words = gameName.split(/\s+/);
          return words.includes(searchLower);
        });
        
        if (wordMatch) {
          result.push(wordMatch as Game);
          foundNames.add(wordMatch.id);
          continue;
        }
      }
      
      // Processo geral para todos os jogos (populares não encontrados acima e não populares)
      
      // 1. Correspondência exata no nome
      const exactMatch = allFoundGames.find(game => 
        !foundNames.has(game.id) && 
        game.name.toLowerCase() === searchLower
      );
      
      if (exactMatch) {
        result.push(exactMatch as Game);
        foundNames.add(exactMatch.id);
        continue;
      }
      
      // 2. Jogos que começam com o termo e não são expansões
      const baseGames = allFoundGames.filter(game => {
        if (foundNames.has(game.id)) return false;
        
        const gameName = game.name.toLowerCase();
        return (gameName === searchLower || 
                gameName.startsWith(searchLower + ' ')) && 
               !isLikelyExpansion(game.name, searchName);
      });
      
      // Ordenar por comprimento do nome (favorecendo nomes mais curtos)
      baseGames.sort((a, b) => a.name.length - b.name.length);
      
      if (baseGames.length > 0) {
        result.push(baseGames[0] as Game);
        foundNames.add(baseGames[0].id);
        continue;
      }
      
      // 3. Jogos que contêm o termo como palavra separada, não como substring
      const wordMatches = allFoundGames.filter(game => {
        if (foundNames.has(game.id)) return false;
        
        const gameName = game.name.toLowerCase();
        // Verificar se o nome contém o termo como palavra completa
        const words = gameName.split(/\s+/);
        return words.includes(searchLower) && 
               !isLikelyExpansion(game.name, searchName);
      });
      
      wordMatches.sort((a, b) => a.name.length - b.name.length);
      
      if (wordMatches.length > 0) {
        result.push(wordMatches[0] as Game);
        foundNames.add(wordMatches[0].id);
        continue;
      }
      
      // 4. Em último caso, qualquer jogo que contenha o termo e não seja expansão
      const containingGames = allFoundGames.filter(game => {
        if (foundNames.has(game.id)) return false;
        
        const gameName = game.name.toLowerCase();
        return gameName.includes(searchLower) && 
               !isLikelyExpansion(game.name, searchName);
      });
      
      containingGames.sort((a, b) => a.name.length - b.name.length);
      
      if (containingGames.length > 0) {
        result.push(containingGames[0] as Game);
        foundNames.add(containingGames[0].id);
        continue;
      }
      
      // 5. Absolutamente último recurso: qualquer jogo que contenha o termo
      const anyMatch = allFoundGames.find(game => 
        !foundNames.has(game.id) && 
        game.name.toLowerCase().includes(searchLower)
      );
      
      if (anyMatch) {
        result.push(anyMatch as Game);
        foundNames.add(anyMatch.id);
      }
    }
    
    console.log(`[ONBOARDING] Encontrados ${result.length} jogos dos ${gameNames.length} nomes sugeridos`);
    return result;
  } catch (error) {
    console.error("[ONBOARDING] Erro ao buscar jogos por nome:", error);
    return [];
  }
}

/**
 * Interface para carregar jogos populares para o onboarding
 */
export async function loadPopularGamesForOnboarding(count: number): Promise<Game[]> {
  try {
    // Buscar jogos ordenados por número de usuários que os possuem na coleção
    const { data, error } = await supabase
      .from('games')
      .select('*')
      .not('stats', 'is', null)
      .order('stats->owned_count', { ascending: false })
      .limit(count);

    if (error) {
      console.error("[ONBOARDING] Erro ao buscar jogos populares:", error);
      return [];
    }

    // Se não houver dados via stats, tentar uma abordagem alternativa
    if (!data || data.length === 0) {
      const { data: fallbackData, error: fallbackError } = await supabase
        .from('games')
        .select('*')
        .order('updated_at', { ascending: false })
        .limit(count);

      if (fallbackError) {
        console.error("[ONBOARDING] Erro ao buscar jogos alternativos:", fallbackError);
        return [];
      }

      return fallbackData as Game[];
    }

    return data as Game[];
  } catch (error) {
    console.error("[ONBOARDING] Erro ao buscar jogos populares:", error);
    return [];
  }
} 